/**
 * The Versichertenstatus of a GKV Verordnung (#1506) — three stored parts, one
 * typed input, and the padding rule that decides which Summenstatus the money
 * lands in.
 *
 * **Why three parts and not one string.** The printed `Status` box and the
 * Muster-13 barcode are two renderings of the *same* three KVDT objects:
 * Technisches Handbuch BFB §2.11 requires the barcode to use, "ebenso wie bei
 * der Bedruckung des Personalienfeldes", the KVDT reference format of the
 * Technische Anlage zu Anlage 4a (BMV-Ä), and even a KVK has to be transformed
 * into that format first. Anlage 4a Tabelle 9 then fixes the positions —
 * `Versichertenart` at 1, `BesonderePersonengruppe` at 2–3,
 * `DMP_Kennzeichnung` at 4–5, `Kennzeichen Rechtsgrundlage` at 6–7, the last of
 * which §302 discards. So the composition is **total**: there is no Bedruckung
 * whose Stellen 1–5 the three barcode fields cannot reconstruct.
 *
 * Research: `docs/wayfinder/1382-versichertenstatus.md`.
 *
 * ⚠ Nothing in this file may reject a value. Every check warns. An unknown
 * Versichertenstatus lands in Summenstatus `99` — "nicht zuzuordnende Status",
 * a *defined* bucket — and drags a NAD-Anschrift requirement with it; the file
 * stays valid. Refusing it would be stricter than the spec and would hold up
 * revenue on a legitimate Verordnung.
 */
/**
 * Anlage 4a maps the eGK's `///Versichertenart` straight through, and the
 * barcode's Feld 10 is `FELDLÄNGE 1`, `TYP n`, `ERLAUBTE INHALTE: 1,3,5`.
 *
 * ⚠ Stelle 1 is the only part of this field with arithmetic consequences: the
 * `SLGA.GES` Summenstatus buckets on it alone (`1`→`11`, `3`→`31`, `5`→`51`,
 * everything else →`99`). The other two parts never move money.
 */
export const VERSICHERTENART_VALUES = ['1', '3', '5'];
/**
 * Anlage 4a Tabelle 2 / Standardbarcode Typ 1 Feld 11 — closed at six values:
 * `00` nicht vorhanden · `04` BSHG §264 SGB V · `06` SER · `07` SVA nach
 * Aufwand · `08` SVA pauschal · `09` AsylbLG.
 */
export const BESONDERE_PERSONENGRUPPE_VALUES = ['00', '04', '06', '07', '08', '09'];
/**
 * ⚠ **The DMP set is deliberately not a literal list.** Technisches Handbuch
 * BFB 4.80 (in Kraft 01.10.2026) added `13` to the Standardbarcode value sets,
 * and Anlage 4a v1.18 still stops at `12` — the meaning of `13` is in no
 * reachable KBV or G-BA document. A list copied out of Anlage 4a would start
 * warning on conformant barcodes the day 4.80 takes effect. Two digits is the
 * only stable statement the sources support.
 */
export const DMP_KENNZEICHNUNG_PATTERN = /^\d{2}$/;
/** How long the printed `Status` box can be: Stellen 1–7 of Druckzeile 6. */
export const VERSICHERTENSTATUS_MAX_LENGTH = 7;
/** How many Stellen §302 transmits in `INV.Versichertenstatus`. */
export const VERSICHERTENSTATUS_TRANSMITTED_LENGTH = 5;
const trimmedOrNull = (value) => {
    const trimmed = value?.trim() ?? '';
    return trimmed.length > 0 ? trimmed : null;
};
/**
 * Whether a two-character part is long enough to be judged at all.
 *
 * ⚠ **Too short is not the same as wrong.** `BesonderePersonengruppe` and
 * `DMP_Kennzeichnung` are `FELDLÄNGE 2`, so a one-character value is a part
 * nobody has finished typing — not a value outside its set. Judging it anyway
 * makes the banner fire in the middle of a perfectly legitimate `5080601`,
 * which passes through `50` on the way, and a banner that cries wolf while you
 * type is a banner nobody reads by the time it is right.
 */
const isComplete = (value) => value !== null && value.length >= 2;
/**
 * Split one typed or printed `Status` into its three parts, positionally.
 *
 * ⚠ Positional, not clever. A half-read `18` yields a one-character besondere
 * Personengruppe `8`, because the alternatives — guessing `08` or `80` — are
 * two *different* Personengruppen and we were only shown one character.
 * Whatever is short warns downstream and still saves.
 *
 * ⚠ The input is alphanumeric. `INV.Versichertenstatus` is typed `AN` because
 * the retired KVK Stelle 5 could be a letter (`M`, `X`, `A`, `C`, …), and a
 * handwritten status is a legitimate transcription. **Never put a digits-only
 * validator on the way in.** We only ever *write* digits.
 */
export function deriveVersichertenstatusParts(versichertenstatus) {
    const raw = versichertenstatus?.trim() ?? '';
    return {
        versichertenart: trimmedOrNull(raw.slice(0, 1)),
        besonderePersonengruppe: trimmedOrNull(raw.slice(1, 3)),
        dmpKennzeichnung: trimmedOrNull(raw.slice(3, 5)),
    };
}
/**
 * Render the five Stellen §302 transmits, or `null` when nothing is known.
 *
 * Built here with its test; **its caller is TM-9539's serialiser and does not
 * exist yet.**
 *
 * Two rules, in this order:
 *
 * 1. **A raw transcription of exactly five characters wins verbatim.** §302
 *    asks for a transcription — "anzugeben ist der Versichertenstatus von der
 *    Verordnung … alle lesbaren Zeichen" — so a status that was already five
 *    characters goes out as it was read, not as we would have reassembled it.
 * 2. Otherwise the parts are concatenated in Anlage 4a order (falling back to
 *    the raw string when no part is set), cut to five and **right-padded**.
 *
 * ⚠ **`padEnd`, never `padStart`.** The §302 text says the legible characters
 * are "ggfs. auf 5 Stellen rechtsbündig mit Nullen ergänzt" — and then pads
 * both of its own examples on the right (`1`→`10000`, `18`→`18000`). Taken
 * literally as right-*alignment*, a legible `1` becomes `00001`, whose Stelle 1
 * is `0`; that is not a Versichertenart, and the Abrechnungsfall drops from
 * Summenstatus `11` into `99`. Right-padding is also semantically the identity,
 * because `00` is the neutral "nicht vorhanden" code in *both* padded
 * positions — there is no eGK-era value the rule destroys.
 */
export function composeVersichertenstatus(input) {
    const raw = trimmedOrNull(input.versichertenstatus) ?? '';
    if (raw.length === VERSICHERTENSTATUS_TRANSMITTED_LENGTH)
        return raw;
    const fromParts = [input.versichertenart, input.besonderePersonengruppe, input.dmpKennzeichnung]
        .map((part) => trimmedOrNull(part) ?? '')
        .join('');
    const source = fromParts.length > 0 ? fromParts : raw;
    if (source.length === 0)
        return null;
    return source
        .slice(0, VERSICHERTENSTATUS_TRANSMITTED_LENGTH)
        .padEnd(VERSICHERTENSTATUS_TRANSMITTED_LENGTH, '0');
}
/**
 * Everything that looks wrong about a Versichertenstatus, as typed copy.
 *
 * ⚠ **These are warnings and only ever warnings.** All three value sets are
 * published and closed, which makes this the cheapest oracle-free guard this
 * path can have — but a KVK-era or handwritten status will legitimately fail
 * them, and refusing it would block billing on a valid Verordnung. An absent
 * part is silent: not having typed a status yet is not a mistake.
 *
 * ⚠ And neither is a half-typed one. The two-character parts are only judged
 * once they *have* two characters — see {@link isComplete}.
 */
export function versichertenstatusProblems(parts) {
    const problems = [];
    const versichertenart = trimmedOrNull(parts.versichertenart);
    if (versichertenart !== null &&
        !VERSICHERTENART_VALUES.includes(versichertenart)) {
        problems.push({
            code: 'VERSICHERTENART_OUTSIDE_VALUE_SET',
            field: 'versichertenart',
            value: versichertenart,
            message: `Die Versichertenart „${versichertenart}“ ist keine der bekannten Angaben 1 (Mitglied), 3 (Familienversichert) oder 5 (Rentner:in).`,
        });
    }
    const besonderePersonengruppe = trimmedOrNull(parts.besonderePersonengruppe);
    if (isComplete(besonderePersonengruppe) &&
        !BESONDERE_PERSONENGRUPPE_VALUES.includes(besonderePersonengruppe)) {
        problems.push({
            code: 'BESONDERE_PERSONENGRUPPE_OUTSIDE_VALUE_SET',
            field: 'besonderePersonengruppe',
            value: besonderePersonengruppe,
            message: `Die besondere Personengruppe „${besonderePersonengruppe}“ steht nicht in der Werteliste (${BESONDERE_PERSONENGRUPPE_VALUES.join(', ')}).`,
        });
    }
    const dmpKennzeichnung = trimmedOrNull(parts.dmpKennzeichnung);
    if (isComplete(dmpKennzeichnung) && !DMP_KENNZEICHNUNG_PATTERN.test(dmpKennzeichnung)) {
        problems.push({
            code: 'DMP_KENNZEICHNUNG_NOT_TWO_DIGITS',
            field: 'dmpKennzeichnung',
            value: dmpKennzeichnung,
            message: `Die DMP-Kennzeichnung „${dmpKennzeichnung}“ besteht nicht aus zwei Ziffern.`,
        });
    }
    return problems;
}
/**
 * The code Anlage 4a uses for a two-digit card part that is **not present**.
 *
 * Both value tables open with "[Element nicht vorhanden] → 00", so downstream of
 * the transformation there is no absent besondere Personengruppe; there is one
 * of `00`.
 */
export const BARCODE_TWO_DIGIT_PART_ABSENT = '00';
/**
 * Normalize a two-digit Muster-13 card field — Feld 11
 * (`BesonderePersonengruppe`) or Feld 12 (`DMP_Kennzeichnung`) — as it came off
 * the barcode (#1508).
 *
 * ⚠ **An empty field is the emitter's mistake, not a shape we carry forward.**
 * Both fields are `FELDLÄNGE 2` — not `<=2`, which is the notation the Handbuch
 * uses for variable lengths — and both value tables code absence as `00`. So
 * `'' → '00'`, which is at once the conformant value *and* what the §302
 * padding rule would have produced anyway.
 *
 * ⚠ Whether a real PVS ever emits a blank Feld 11/12 is **unverified**: the rule
 * is derived from the spec, not from observed scans, and our own e2e fixtures
 * are no evidence either way.
 *
 * Everything else passes through trimmed and unjudged — including a short or
 * out-of-set value. Judging is {@link versichertenstatusProblems}'s job, and it
 * only ever warns.
 */
export function normalizeBarcodeTwoDigitPart(value) {
    return trimmedOrNull(value) ?? BARCODE_TWO_DIGIT_PART_ABSENT;
}
/**
 * What to put in the one printed `Status` box when a card arrived from a scan
 * (#1508), or `null` when the barcode gave us nothing legible to put there.
 *
 * The scanned path carries the three KVDT parts and **no printed
 * transcription** — nobody read a box off the paper — so the box is *seeded*
 * from the parts rather than left empty, and the seeded value is deliberately
 * not written back as a transcription (§302 asks for „alle lesbaren Zeichen"
 * *von der Verordnung*).
 *
 * ⚠ **A blank Feld 10 yields nothing, not `00000`.** Feld 10 is `FELDLÄNGE 1`
 * with `ERLAUBTE INHALTE 1,3,5` and has no code for "not present", so unlike
 * Felder 11/12 there is nothing to normalize a blank one into. Composing
 * positionally anyway would render `00000`, whose Stelle 1 is `0` — not a
 * Versichertenart, and enough to drop the Abrechnungsfall out of Summenstatus
 * `11` into `99`. That is the same mistake as `padStart`, arrived at from the
 * other side, so the box stays empty and a Therapeut:in types the Status off
 * the sheet.
 *
 * ⚠ **And nothing at all when any field is the wrong width**, which is the same
 * rule seen from further along. The composition into one box is positional, so
 * it is faithful only while each part occupies exactly its declared Stellen:
 * Feld 10 is `FELDLÄNGE 1`, Felder 11 and 12 are `FELDLÄNGE 2`. A
 * non-conformant one-character Feld 11 of `8` would compose to `18000` and
 * derive back as besondere Personengruppe `80` — a *different* Personengruppe
 * than the barcode carried, written silently. {@link
 * normalizeBarcodeTwoDigitPart} deliberately does not pad it, because `08` and
 * `80` are two different Personengruppen and we were shown one character; the
 * same refusal to guess has to hold here.
 *
 * ⚠ It does **not** judge the *value*: an out-of-set Versichertenart of the
 * right width is rendered as read and warned about by {@link
 * versichertenstatusProblems}.
 */
export function versichertenstatusBoxFromCard(parts) {
    const widths = [
        [trimmedOrNull(parts.versichertenart), 1],
        [trimmedOrNull(parts.besonderePersonengruppe), 2],
        [trimmedOrNull(parts.dmpKennzeichnung), 2],
    ];
    if (widths.some(([value, width]) => value === null || value.length !== width))
        return null;
    return composeVersichertenstatus(parts);
}
//# sourceMappingURL=versichertenstatus.js.map